// src/pages/reports/SalesSummary.js
import React, { useEffect, useState, useMemo } from "react";
import Layout from "../../components/Layout";
import { db } from "../../firebase";

import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";


// --- PREMIUM UI TOKENS (Standardized for Reports) ---
const UI_THEME = {
  bg: "#f8fafc",
  surface: "#ffffff",
  primary: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
  secondary: "#f1f5f9",
  accent: "#10b981",
  text: "#0f172a",
  muted: "#64748b",
  border: "#e2e8f0",
  shadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
};

const filterCardStyle = {
  background: UI_THEME.surface,
  padding: "24px",
  borderRadius: "16px",
  boxShadow: UI_THEME.shadow,
  border: `1px solid ${UI_THEME.border}`,
  display: "flex",
  gap: "20px",
  alignItems: "flex-end",
  marginBottom: "32px",
  flexWrap: "wrap",
};

const inputGroupStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const labelStyle = {
  fontSize: "12px",
  fontWeight: 800,
  color: UI_THEME.muted,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const inputStyle = {
  padding: "10px 14px",
  borderRadius: "10px",
  border: `1px solid ${UI_THEME.border}`,
  fontSize: "14px",
  fontWeight: 600,
  color: UI_THEME.text,
  outline: "none",
  minWidth: "160px",
};

const primaryBtn = {
  padding: "10px 24px",
  borderRadius: "10px",
  background: UI_THEME.primary,
  color: "#fff",
  border: "none",
  fontWeight: 700,
  fontSize: "14px",
  cursor: "pointer",
  boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.4)",
};

const secondaryBtn = {
  padding: "10px 20px",
  borderRadius: "10px",
  background: "#f1f5f9",
  color: "#475569",
  border: "none",
  fontWeight: 700,
  fontSize: "14px",
  cursor: "pointer",
};

const tableHeaderStyle = {
  padding: "16px 20px",
  background: "#f8fafc",
  color: "#64748b",
  fontSize: "11px",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  borderBottom: `2px solid ${UI_THEME.border}`,
  textAlign: "left",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const tableCellStyle = {
  padding: "14px 20px",
  fontSize: "14px",
  color: UI_THEME.text,
  borderBottom: `1px solid #f1f5f9`,
};

function formatDDMMYYYY(ymd) {
  if (!ymd) return "";
  const [yyyy, mm, dd] = ymd.split("-");
  return `${dd}-${mm}-${yyyy}`;
}

function normalizeDate(value) {
  if (!value) return "";
  if (value?.seconds) {
    const d = new Date(value.seconds * 1000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  if (value instanceof Date) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
  }
  return String(value);
}

function formatNumber(v) {
  return Number(v || 0).toFixed(2);
}

function todayDateOnly() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function SalesSummary() {
  // filters
  const [fromDate, setFromDate] = useState(""); 
  
  
  const [toDate, setToDate] = useState("");
  const [searchText, setSearchText] = useState("");

  // data
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]); // aggregated rows
  const [allRows, setAllRows] = useState([]); // full aggregated for export
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  // totals
  const [totals, setTotals] = useState({
    BasicAmount: 0,
    GST_Amount: 0,
    CessAmount: 0,
    AddlCessAmount: 0,
    ewayAmount: 0,
    RebateAmount: 0,
    GrandTotal: 0,
  });

  useEffect(() => {
  fetchAndAggregate();     // auto fetch when component loads AND when dates change
}, [fromDate, toDate]);


  // Fetch orders in date range and aggregate using orderLines
  const fetchAndAggregate = async (opts = {}) => {
    try {
      setLoading(true);
      setPage(1);

      const from = opts.fromDate || fromDate;
      const to = opts.toDate || toDate;

      // Firestore query: status == "APPROVED" and orderDateOnly between
      // Note: ensure composite index on (status, orderDateOnly)
      const ordersRef = collection(db, "orders");
const q = query(
  collection(db, "orders"),
  where("status", "==", "APPROVED"),
  where("approvedDateOnly", ">=", fromDate),
  where("approvedDateOnly", "<=", toDate)
);

      const snap = await getDocs(q);
      const rowsMap = new Map(); // key: `${date}__${partyName}`

      // iterate orders and expand frozen orderLines
      snap.docs.forEach((docSnap) => {
        const o = docSnap.data();

        // ensure orderLines exists
        const lines = Array.isArray(o.orderLines) ? o.orderLines : [];

        // party type/name
        const partyName = o.partyName || o.name || o.partyId || "";
        const partyType = o.partyType || o.partytype || o.partyCategory || "";
        const paymentMode = o.payment_mode || "";

        lines.forEach((L) => {
          // Use frozen date if present, else fallback to order.orderDateOnly
          const date = normalizeDate(o.approvedAt);


          const key = `${date}__${partyName}`;

          // per-line values (we expect these to be frozen numbers)
          const basicAmount = Number(L.basicAmount || 0);
          const gstAmount = Number(L.gstAmount || 0);
          const cessAmount = Number(L.cessAmount || 0);
          const addlCessAmount = Number(L.addlCessAmount || 0);
          const ewayAmount = Number(L.ewayAmount || 0);
          const rebateAmount = Number(L.rebateAmount || 0);
          const grandTotal = ewayAmount + rebateAmount;

          if (!rowsMap.has(key)) {
            rowsMap.set(key, {
              date,
              partyType,
              partyName,
              paymentMode,
              BasicAmount: 0,
              GST_Amount: 0,
              CessAmount: 0,
              AddlCessAmount: 0,
              ewayAmount: 0,
              RebateAmount: 0,
              GrandTotal: 0,
            });
          }

          const g = rowsMap.get(key);
          g.BasicAmount += basicAmount;
          g.GST_Amount += gstAmount;
          g.CessAmount += cessAmount;
          g.AddlCessAmount += addlCessAmount;
          g.ewayAmount += ewayAmount;
          g.RebateAmount += rebateAmount;
          g.GrandTotal += grandTotal;
        });
      });

      // convert map to array and sort by date, partyName
      const rows = Array.from(rowsMap.values()).sort((a, b) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
        return a.partyName.localeCompare(b.partyName);
      });

      // set state
      setAllRows(rows);
      setGroups(rows.slice(0, PAGE_SIZE));
      computeTotals(rows);
    } catch (err) {
      console.error("Failed to fetch orders:", err);
      alert("Error fetching orders for reports. See console.");
    } finally {
      setLoading(false);
    }
  };

  const computeTotals = (rows) => {
    const t = {
      BasicAmount: 0,
      GST_Amount: 0,
      CessAmount: 0,
      AddlCessAmount: 0,
      ewayAmount: 0,
      RebateAmount: 0,
      GrandTotal: 0,
    };

    rows.forEach((r) => {
      t.BasicAmount += Number(r.BasicAmount || 0);
      t.GST_Amount += Number(r.GST_Amount || 0);
      t.CessAmount += Number(r.CessAmount || 0);
      t.AddlCessAmount += Number(r.AddlCessAmount || 0);
      t.ewayAmount += Number(r.ewayAmount || 0);
      t.RebateAmount += Number(r.RebateAmount || 0);
      t.GrandTotal += Number(r.GrandTotal || 0);
    });

    setTotals(t);
  };

  // when search text changes or page changes, compute visible groups
  const visibleRows = useMemo(() => {
    const txt = (searchText || "").trim().toLowerCase();
    const filtered = allRows.filter((r) => {
      if (!txt) return true;
      // search partyName, partyType, date fields
      return (
        (r.partyName || "").toLowerCase().includes(txt) ||
        (r.partyType || "").toLowerCase().includes(txt) ||
        (r.date || "").toLowerCase().includes(txt)
      );
    });

    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [allRows, searchText, page]);

  // re-calc groups shown when visibleRows changes
  useEffect(() => {
    setGroups(visibleRows);
  }, [visibleRows]);

  // ACTIONS
  const onSearch = (e) => {
    setSearchText(e.target.value);
    setPage(1);
  };

  const onFilterApply = async () => {
    await fetchAndAggregate({ fromDate: fromDate, toDate: toDate });
  };

  const onExportExcel = () => {
    // Export full allRows (not just page)
    const exportRows = allRows.map((r) => ({
      Date: r.date,
      "Party Type": r.partyType,
      Mode: r.paymentMode,
      "Party Name": r.partyName,
      BasicAmount: Number(r.BasicAmount || 0),
      GST_Amount: Number(r.GST_Amount || 0),
      CessAmount: Number(r.CessAmount || 0),
      AddlCessAmount: Number(r.AddlCessAmount || 0),
      ewayAmount: Number(r.ewayAmount || 0),
      RebateAmount: Number(r.RebateAmount || 0),
      GrandTotal: Number(r.GrandTotal || 0),
    }));

    // Build worksheet & workbook using xlsx
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SalesSummary");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `SalesSummary_${fromDate}_to_${toDate}.xlsx`);
  };

  const onPrev = () => {
    setPage((p) => Math.max(1, p - 1));
  };

  const onNext = () => {
    const totalPages = Math.ceil(
      (allRows.filter((r) =>
        searchText
          ? (r.partyName || "").toLowerCase().includes(searchText.toLowerCase()) ||
            (r.partyType || "").toLowerCase().includes(searchText.toLowerCase()) ||
            (r.date || "").toLowerCase().includes(searchText.toLowerCase())
          : true
      ).length || 0) / PAGE_SIZE
    );
    setPage((p) => Math.min(totalPages || 1, p + 1));
  };

  return (
    <Layout>
      <div style={{ padding: "32px", background: UI_THEME.bg, minHeight: "calc(100vh - 80px)" }}>
        
        {/* PAGE HEADER */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "32px" }}>
          <div>
            <h1 style={{ fontSize: "28px", fontWeight: 800, color: UI_THEME.text, margin: 0 }}>Sales Summary</h1>
            <p style={{ color: UI_THEME.muted, fontWeight: 500, marginTop: "4px" }}>Performance tracking & analytical breakdown</p>
          </div>
          <button onClick={onExportExcel} style={secondaryBtn}>
            📥 Export to Excel
          </button>
        </div>

        {/* FILTER CARD */}
        <div style={filterCardStyle}>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Date From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputStyle} />
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Date To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputStyle} />
          </div>
          <button onClick={onFilterApply} style={primaryBtn}>Generate</button>
          
          <div style={{ ...inputGroupStyle, marginLeft: "auto", flex: 1, maxWidth: "400px" }}>
            <label style={labelStyle}>Search results</label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", opacity: 0.5 }}>🔍</span>
              <input 
                placeholder="Search party, type or date..." 
                value={searchText} 
                onChange={onSearch} 
                style={{ ...inputStyle, width: "100%", paddingLeft: "36px" }} 
              />
            </div>
          </div>
        </div>

        {/* DATA TABLE CARD */}
        <div style={{ 
          background: UI_THEME.surface, 
          borderRadius: "16px", 
          boxShadow: UI_THEME.shadow, 
          border: `1px solid ${UI_THEME.border}`,
          overflow: "hidden"
        }}>
          <div style={{ overflowX: "auto", maxHeight: "600px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1200px" }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Date</th>
                  <th style={tableHeaderStyle}>Type</th>
                  <th style={tableHeaderStyle}>Mode</th>
                  <th style={tableHeaderStyle}>Party Name</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Basic</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>GST</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Cess</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Addl</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Eway</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Rebate</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "right" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" style={{ padding: "100px", textAlign: "center", color: UI_THEME.muted }}>
                      <div style={{ fontSize: "24px", marginBottom: "16px" }}>⏳</div>
                      Fetching latest summaries...
                    </td>
                  </tr>
                ) : groups.length === 0 ? (
                  <tr>
                    <td colSpan="11" style={{ padding: "100px", textAlign: "center", color: UI_THEME.muted }}>
                      <div style={{ fontSize: "24px", marginBottom: "16px" }}>📭</div>
                      No data found for the selected range.
                    </td>
                  </tr>
                ) : (
                  groups.map((r, idx) => (
                    <tr key={idx} style={{ 
                      background: idx % 2 === 0 ? "#fff" : "#fafafa",
                      transition: "background 0.2s"
                    }}>
                      <td style={tableCellStyle}>{formatDDMMYYYY(r.date)}</td>
                      <td style={{ ...tableCellStyle, fontWeight: 700 }}>{r.partyType}</td>
                      <td style={tableCellStyle}>
                        {r.paymentMode ? (
                          <span style={{ 
                            padding: "4px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: 800,
                            background: r.paymentMode === "Credit" ? "#fef3c7" : "#dcfce7",
                            color: r.paymentMode === "Credit" ? "#92400e" : "#166534"
                          }}>
                            {r.paymentMode}
                          </span>
                        ) : null}
                      </td>
                      <td style={tableCellStyle}>{r.partyName}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatNumber(r.BasicAmount)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatNumber(r.GST_Amount)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatNumber(r.CessAmount)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatNumber(r.AddlCessAmount)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatNumber(r.ewayAmount)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatNumber(r.RebateAmount)}</td>
                      <td style={{ ...tableCellStyle, textAlign: "right", fontWeight: 700, color: "#4f46e5", fontVariantNumeric: "tabular-nums" }}>{formatNumber(r.GrandTotal)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {!loading && groups.length > 0 && (
                <tfoot style={{ background: "#f8fafc", borderTop: `2px solid ${UI_THEME.border}`, position: "sticky", bottom: 0 }}>
                  <tr style={{ fontWeight: 800, color: UI_THEME.text }}>
                    <td colSpan="4" style={{ ...tableCellStyle, textAlign: "right" }}>GRAND TOTAL</td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>{formatNumber(totals.BasicAmount)}</td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>{formatNumber(totals.GST_Amount)}</td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>{formatNumber(totals.CessAmount)}</td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>{formatNumber(totals.AddlCessAmount)}</td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>{formatNumber(totals.ewayAmount)}</td>
                    <td style={{ ...tableCellStyle, textAlign: "right" }}>{formatNumber(totals.RebateAmount)}</td>
                    <td style={{ ...tableCellStyle, textAlign: "right", color: "#4f46e5", fontSize: "16px" }}>₹{formatNumber(totals.GrandTotal)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* PAGINATION BAR */}
          <div style={{ padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${UI_THEME.border}` }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: UI_THEME.muted }}>
              Showing {groups.length} of {allRows.length} results
            </span>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button 
                onClick={onPrev} 
                disabled={page === 1} 
                style={{ ...secondaryBtn, padding: "8px 16px", opacity: page === 1 ? 0.5 : 1 }}
              >
                Previous
              </button>
              <span style={{ fontWeight: 700, fontSize: "14px", color: UI_THEME.text }}>Page {page}</span>
              <button 
                onClick={onNext} 
                style={{ ...secondaryBtn, padding: "8px 16px", opacity: groups.length < PAGE_SIZE ? 0.5 : 1 }}
                disabled={groups.length < PAGE_SIZE}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
